/* Destination semantics shared by every screen.

   The backend gives each purchase line a split, e.g. {out: 1, crossdock: 1,
   stock: 3}. Cartons are assigned in that order as they are scanned: the
   first unit ships, the next is held, the rest is put away. */

export const KINDS = {
  out: { word: 'OUT', title: 'Ship now', tone: 'tone-out' },
  hold: { word: 'HOLD', title: 'Set aside for now', tone: 'tone-crossdock' },
  crossdock: { word: 'CROSSDOCK', title: 'Hold for order', tone: 'tone-crossdock' },
  stock: { word: 'STOCK', title: 'Put away', tone: 'tone-stock' },
  over: { word: 'EXTRA', title: 'Not expected', tone: 'tone-over' },
  unknown: { word: 'UNKNOWN', title: 'Not on this order', tone: 'tone-unknown' },
};

const ORDER = ['out', 'hold', 'crossdock', 'stock'];

/** Which destination does the n-th scanned unit (1-based) of a line go to? */
export function unitVerdict(dest, n) {
  if (!dest?.split) return 'stock';
  let acc = 0;
  for (const kind of ORDER) {
    const q = Number(dest.split[kind] || 0);
    if (n <= acc + q) return kind;
    acc += q;
  }
  return 'stock';
}

/** The customer orders behind a verdict kind, most urgent first. */
export function ordersFor(dest, kind) {
  if (!dest?.orders) return [];
  return dest.orders.filter((o) => o.kind === kind);
}

/** Compact chips for a line: [{kind, qty}] in display order. */
export function splitChips(dest) {
  if (!dest?.split) return [];
  return ORDER.filter((k) => dest.split[k] > 0).map((k) => ({ kind: k, qty: dest.split[k] }));
}

/** Try the code as-is, then the usual UPC-A / GTIN-14 leading-zero variants. */
export function resolveBarcode(map, raw) {
  const code = String(raw || '').trim();
  if (!code) return { code, entries: null };
  const candidates = [code];
  if (/^\d{12}$/.test(code)) candidates.push(`0${code}`);
  if (/^0\d{12}$/.test(code)) candidates.push(code.slice(1));
  if (/^0\d{13}$/.test(code)) candidates.push(code.slice(1));
  if (/^\d{14}$/.test(code) && code[0] !== '0') candidates.push(code.slice(1));
  for (const c of candidates) {
    if (map?.[c]?.length) return { code: c, entries: map[c] };
  }
  return { code, entries: null };
}

/* The backend classifies as if the whole delivery arrives. On the floor only
   what was scanned counts: an OUT that depends on another line of this
   delivery stays CROSSDOCK until that line has been counted. `counts` is
   the session's units per line. */
export function liveDest(dest, counts = {}, { final = false } = {}) {
  if (!dest?.orders?.length) return dest;
  let changed = false;
  const orders = dest.orders.map((o) => {
    if (o.kind !== 'out' || !o.depends_on?.length) return o;
    const missing = o.depends_on.filter((d) => Number(counts[d.line_id] || 0) < Number(d.qty || 1));
    if (!missing.length) return { ...o, arrived_with: o.depends_on.map((d) => d.name) };
    changed = true;
    // While scanning: HOLD, set it aside. At confirmation the truck is done: CROSSDOCK.
    return { ...o, kind: final ? 'crossdock' : 'hold', pending_delivery: true, waiting_on: missing.map((d) => d.name) };
  });
  if (!changed) return { ...dest, orders };
  const split = { out: 0, hold: 0, crossdock: 0, stock: dest.split?.stock || 0 };
  orders.forEach((o) => {
    split[o.kind] = (split[o.kind] || 0) + Number(o.qty || 0);
  });
  const clean = Object.fromEntries(Object.entries(split).filter(([, v]) => v > 0));
  const primary = clean.out ? 'out' : clean.hold ? 'hold' : clean.crossdock ? 'crossdock' : 'stock';
  return { ...dest, orders, split: clean, primary };
}
